import { NavPage } from '@/components/breadcrumbs/Breadcrumbs';
import { Button } from '@/components/form/Button';
import { ButtonVariant } from '@/components/form/buttonHelper';
import { Loader } from '@/components/loading/Loading';
import { useModal } from '@/components/modal/useModal';
import BreadcrumbPageHeader from '@/layouts/BreadcrumbPageHeader';
import { Container } from '@/layouts/main/Main';
import { FinanceModelTypes } from '@/services/api/contracts/financemodels';
import { useFinanceModels, usePeripheralFinanceModels } from '@/services/api/hooks/financemodels';
import { RoleType, useRole } from '@/services/authentication/hooks/user';
import { FC, FunctionComponent, Suspense, useEffect, useMemo, useState } from 'react';
import { FiSettings } from 'react-icons/fi';
import { useNavigate } from 'react-router';
import BulkUploadFinanceModelModal from '../bulk/BulkUploadFinanceModelModal';
import { DefaultModelsModal } from './DefaultModelsModal';
import DownloadFinanceModels from './DownloadFinanceModels';
import FinanceModelFilters from './FinanceModelFilters';
import FinanceModelModal from './FinanceModelModal';
import FinanceModelsTable, { FinanceModelsTableData } from './FinanceModelsTable';
import { DEFAULT_SORT_OPTION, FinanceModelFilterOptions, PERIPHERAL_CAT_FILTER_ID } from './filter/helper';
import PeripheralFinanceModelModal from './peripheral/PeripheralFinanceModelModal';

interface ContentProps {
  onEditCallback: (modelId: string, isPeripheral: boolean) => void;
}

const Content: FunctionComponent<ContentProps> = ({ onEditCallback }) => {
  const models = useFinanceModels();

  const peripheralModels = usePeripheralFinanceModels();

  const deviceModelTableData = useMemo(() => {
    return models.map(model => {
      return {
        id: model.id,
        categoryName: model.category.name ?? '',
        manufacturerName: model.manufacturer.name ?? '',
        term: model.term,
        type: model.financeModelType,
        pcms: model.pcms,
        value: model.value || 0,
        name: model.name
      };
    }) as FinanceModelsTableData[];
  }, [models]);

  const peripheralModelTableData = useMemo(() => {
    return peripheralModels.map(model => {
      return {
        id: model.id,
        categoryName: 'Peripheral',
        manufacturerName: '',
        term: model.term,
        type: model.peripheralFinanceModelType,
        pcms: model.pcms,
        value: model.value || 0,
        name: model.name,
        isPeripheral: true
      };
    }) as unknown as FinanceModelsTableData[];
  }, [peripheralModels]);

  const [filterOptions, setFilterOptions] = useState<FinanceModelFilterOptions>();

  const filteredData = useMemo(() => {
    const data = [...deviceModelTableData, ...peripheralModelTableData];

    const filterByCategory = (data: FinanceModelsTableData[]) =>
      data.filter(item => {
        if (filterOptions?.category === null || filterOptions?.category.id === DEFAULT_SORT_OPTION.id) return true;

        if (filterOptions?.category?.id === PERIPHERAL_CAT_FILTER_ID) {
          return item.isPeripheral;
        } else {
          return item.categoryName === filterOptions?.category.name;
        }
      });

    const filterByManufacturer = (data: FinanceModelsTableData[]) =>
      data.filter(item => {
        if (filterOptions?.manufacturer === null || filterOptions?.manufacturer.id === DEFAULT_SORT_OPTION.id)
          return true;

        return item.manufacturerName === filterOptions?.manufacturer.name;
      });

    const filterByType = (data: FinanceModelsTableData[]) =>
      data.filter(item => {
        if (!filterOptions?.type || filterOptions?.type === null || filterOptions?.type.id === DEFAULT_SORT_OPTION.id)
          return true;

        return item.type === (Number(filterOptions?.type.id) as unknown as FinanceModelTypes);
      });

    const filterByTerm = (data: FinanceModelsTableData[]) =>
      data.filter(item => {
        if (filterOptions?.term === null || filterOptions?.term.id === DEFAULT_SORT_OPTION.id) return true;

        return String(item.term) === filterOptions?.term.id;
      });

    return filterByTerm(filterByType(filterByManufacturer(filterByCategory(data))));
  }, [deviceModelTableData, peripheralModelTableData, filterOptions]);

  return (
    <Suspense
      fallback={
        <div className="w-full py-8 flex justify-center items-center">
          <Loader className="h-8 w-8" />
        </div>
      }
    >
      <div className="space-y-3">
        <div className="flex flex-row space-x-3">
          <FinanceModelFilters
            models={models}
            onChange={(options: FinanceModelFilterOptions) => {
              setFilterOptions(options);
            }}
          />
        </div>

        {filteredData.length > 0 ? (
          <FinanceModelsTable
            data={filteredData}
            onEdit={(id: string, isPeripheral: boolean) => {
              onEditCallback(id, isPeripheral);
            }}
          />
        ) : (
          <p className="font-semibold text-lg mt-10">No finance models available</p>
        )}
      </div>
    </Suspense>
  );
};

export const FinanceModelsPage: FC = () => {
  const role = useRole();
  const navigate = useNavigate();

  useEffect(() => {
    if (role.type !== RoleType.Admin) navigate(-1);
  }, [role]);

  const breadcrumbs: NavPage[] = [
    {
      name: 'Finance Models',
      to: ''
    }
  ];

  const modalProps = useModal<{ modelId: string }>();
  const peripheralModalProps = useModal<{ modelId: string }>();
  const bulkUploadModalProps = useModal();
  const defaultsModalProps = useModal();

  return (
    <>
      <BreadcrumbPageHeader breadcrumbs={breadcrumbs}>
        <div className="flex justify-between gap-5 items-center font-bold">
          <h3 className="text-3xl">Finance Models</h3>
          <div className="flex space-x-2">
            <Button
              onClick={() => {
                defaultsModalProps.open();
              }}
              className="space-x-1"
            >
              <FiSettings />
              <span>Set Defaults</span>
            </Button>
            <DownloadFinanceModels />
            <Button
              variant={ButtonVariant.Primary}
              onClick={() => {
                bulkUploadModalProps.open();
              }}
            >
              Upload Models
            </Button>
          </div>
        </div>
      </BreadcrumbPageHeader>
      <div className="flex-1 overflow-y-auto">
        <Container animate="pop" className="w-full py-8">
          <Content
            onEditCallback={(id: string, isPeripheral: boolean) => {
              isPeripheral ? peripheralModalProps.open({ modelId: id }) : modalProps.open({ modelId: id });
            }}
          />
        </Container>
      </div>
      <FinanceModelModal {...modalProps} />
      <PeripheralFinanceModelModal {...peripheralModalProps} />
      <BulkUploadFinanceModelModal {...bulkUploadModalProps} />
      <DefaultModelsModal {...defaultsModalProps} />
    </>
  );
};
